<template>
  <div class="row text-justify-between mb-3 lh-sm">
    <div class="col-12 form-text mb-1" v-html="$t('detailsForm.projection.b1')"></div>
  </div>
  <div class="row">
    <div class="col-12 col-md-12 col-lg-9 col-xl-10">
      <div class="row">
        <!-- Variable -->
        <div class="col-md-12 col-lg-12 col-xl-6">
          <div class="row">
            <div class="col-12">
              <label for="projection_variable" class="form-label">
                <span class="me-1">{{ $t('InputSearchFormLabelTitle.Datatype') }}</span>
                <i class="bi bi-info-circle tooltip-init cursor-pointer me-1"
                   data-bs-toggle="tooltip"
                   data-bs-html="true"
                   @click="tooltipGlossaryOnClick"
                   :data-bs-title='`<small>${$t(tooltipGlossaryVariableKey())} <span class="fw-semibold">${tooltipGlossaryVariableName()}</span></small>`'></i>
                <div class="spinner-grow spinner-grow-sm" role="status" v-if="componentIsLoading === true">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </label>
            </div>
          </div>
          <select id="projection_variable"
                  class="form-select"
                  v-model="variable"
                  ref="projection_variable"
                  :disabled="componentIsLoading === true || searchIsLoading === true">
            <option disabled value="" selected>{{ $t('formDataType.selectDataType') }}</option>
            <template v-for="(item, key) in this.variableMap">
              <option :value="key">{{ item.getNameI18N(this.$i18n.locale) }}</option>
            </template>
          </select>
        </div>

        <!-- Select Season -->
        <div class="col-12 col-md-12 col-lg-12 col-xl-6 mt-2 mt-xl-0">
          <div class="row">
            <div class="col-12">
              <label class="form-label">{{ $t('InputSearchFormLabelTitle.Season') }}
                <i class="bi bi-info-circle tooltip-init"
                   data-bs-toggle="tooltip"
                   data-bs-html="true"
                   :data-bs-title="`<small>${$t('tooltips.Season')}</small>`"></i>
              </label>
            </div>
          </div>
          <div class="btn-group d-flex" role="group">
            <template v-for="seasonItem in constants.SEASONS">
              <input type="radio"
                     name="projection_percentile"
                     class="btn-check"
                     v-model="season"
                     :value="seasonItem"
                     :disabled="this.seasonIsAvailable(seasonItem) === false || componentIsLoading === true || searchIsLoading === true"
                     :id="`projection_season_${seasonItem}`"
                     autocomplete="off">
              <label class="btn btn-outline-primary btn-l mb-0 tooltip-init"
                     data-bs-toggle="tooltip"
                     data-bs-html="true"
                     :data-bs-title="`<small>${$t(`InputSeasonsTooltip.${seasonItem}`)}</small>`"
                     :for="`projection_season_${seasonItem}`">
                <small>{{ $t(`InputSeasons.${seasonItem}`) }}</small></label>
            </template>
          </div>
        </div>

        <!-- Select Year -->
        <div class="col-12 col-md-12 col-lg-12 col-xl-6 mt-2">
          <label for="yearSelect" class="form-label">{{ $t('InputSearchFormLabelTitle.Year') }}
            <i class="bi bi-info-circle tooltip-init"
               data-bs-toggle="tooltip"
               data-bs-html="true"
               :data-bs-title="`<small>${$t('tooltips.Year')}</small>`"></i>
          </label>
          <div class="btn-group d-flex" role="group">
            <input type="radio"
                   name="projection_year"
                   class="btn-check"
                   id="year_2050"
                   value="2050"
                   :disabled="this.yearIsAvailable(2050) === false || componentIsLoading === true || searchIsLoading === true"
                   v-model="year"
                   autocomplete="off">
            <label class="btn btn-outline-primary btn-l mb-0" for="year_2050">2050</label>
            <input type="radio"
                   name="projection_year"
                   class="btn-check"
                   id="year_2075"
                   value="2075"
                   v-model="year"
                   :disabled="this.yearIsAvailable(2075) === false || componentIsLoading === true || searchIsLoading === true"
                   autocomplete="off">
            <label class="btn btn-outline-primary btn-l mb-0" for="year_2075">2075</label>
            <input type="radio"
                   name="projection_year"
                   class="btn-check"
                   id="year_2095"
                   value="2095"
                   v-model="year"
                   :disabled="this.yearIsAvailable(2095) === false || componentIsLoading === true || searchIsLoading === true"
                   autocomplete="off">
            <label class="btn btn-outline-primary btn-l mb-0" for="year_2095">2095</label>
          </div>
        </div>

        <!-- Select Emission -->
        <div class="col-12 col-md-12 col-lg-12 col-xl-6 mt-2">
          <label for="emissionSelect" class="form-label">
            {{ $t('InputSearchFormLabelTitle.climateChangeScenario') }}
            <i class="bi bi-info-circle tooltip-init"
               data-bs-toggle="tooltip"
               data-bs-html="true"
               :data-bs-title="`<small>${$t('tooltips.ClimateScenarios')}</small>`"></i>
          </label>
          <div class="btn-group d-flex" role="group">
            <input type="radio"
                   name="emission"
                   class="btn-check"
                   id="emission_ssp1_2-6"
                   :value="constants.SSPS.SSPS1_2_6"
                   v-model="emissionScenario"
                   :disabled="this.scenarioIsAvailable(constants.SSPS.SSPS1_2_6) === false || componentIsLoading === true || searchIsLoading === true"
                   autocomplete="off">
            <label class="btn btn-outline-success btn-l mb-0 tooltip-init"
                   data-bs-toggle="tooltip"
                   data-bs-html="true"
                   :data-bs-title="`${constants.SSPS.SSPS1_2_6.toUpperCase()}`"
                   for="emission_ssp1_2-6">
              <span class="small">{{ $t('InputSSPLabel.SSP1_2-6') }}</span>
            </label>
            <input type="radio"
                   name="emission"
                   class="btn-check tooltip-init"
                   id="emission_ssp2_4-5"
                   :value="constants.SSPS.SSPS2_4_5"
                   v-model="emissionScenario"
                   :disabled="this.scenarioIsAvailable(constants.SSPS.SSPS2_4_5) === false || componentIsLoading === true || searchIsLoading === true"
                   autocomplete="off">
            <label class="btn btn-outline-warning btn-l mb-0 tooltip-init"
                   data-bs-toggle="tooltip"
                   data-bs-html="true"
                   :data-bs-title="`${constants.SSPS.SSPS2_4_5.toUpperCase()}`"
                   for="emission_ssp2_4-5">
              <span class="small">{{ $t('InputSSPLabel.SSP2_4-5') }}</span>
            </label>
            <input type="radio"
                   name="emission"
                   class="btn-check tooltip-init"
                   id="emission_ssp5_8-5"
                   :value="constants.SSPS.SSPS5_8_5"
                   v-model="emissionScenario"
                   :disabled="this.scenarioIsAvailable(constants.SSPS.SSPS5_8_5) === false || componentIsLoading === true || searchIsLoading === true"
                   autocomplete="off">
            <label class="btn btn-outline-danger btn-l mb-0 tooltip-init"
                   data-bs-toggle="tooltip"
                   data-bs-html="true"
                   :data-bs-title="`${constants.SSPS.SSPS5_8_5.toUpperCase()}`"
                   for="emission_ssp5_8-5">
              <span class="small">{{ $t('InputSSPLabel.SSP5_8-5') }}</span>
            </label>
          </div>
        </div>
      </div>
    </div>
    <!-- Explore button -->
    <div class="col-12 col-md-12 col-lg-3 col-xl-2 mt-3 mt-lg-0 align-self-end">
      <button type="button" class="btn btn-success w-100" :disabled="isSearchDisabled"
              @click="onSearchClick">
        <i v-if="propBlockComponent === true" class="bi bi-pause-circle pe-2"></i>
        <template v-if="propBlockComponent === false">
          <div v-if="searchIsLoading === true" class="spinner-grow spinner-grow-sm me-2" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <i v-if="searchIsLoading === false" class="bi pe-2"
             :class="{
              'bi-compass': isLastUUIDVisible === false,
              'bi-check-circle-fill': isLastUUIDVisible === true,
            }"></i>
        </template>
        <span class="small">{{ $t('ExploreButtonSearchForm.Explore') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import Storage from "@/libs/Storage";
import constants from "@/libs/Constants";
import ApiConnector from "@/libs/ApiConnection";
import GroupErrorTranslation from "@/libs/locales/errors";
import {VariableColorConfigurationInterface} from "@/libs/Colors";
import {disposeTooltipsByClass, setupTooltipsByClass} from "@/libs/Helpers";
import {VariableConfiguration, VisualizationProjectionData} from '@/libs/VisualizationData';

export default {
  emits: ['onSearchClick', 'onSearchClickError', 'onSearchClickWait', 'afterSearchClickResult', 'tooltipGlossaryOnClick'],
  props: {
    propInitializeTooltips: Boolean,
    propLastUuid: String,
    propBlockComponent: Boolean
  },
  data() {
    return {
      variable: '',
      year: '',
      season: '',
      emissionScenario: '',

      visualizationMode: constants.VISUALIZATION_MODE.SINGLE,

      variableMap: {},
      yearsAvailable: [],
      seasonsAvailable: [],
      scenariosAvailable: [],

      //Constants
      constants: constants,

      //Loading
      componentIsLoading: true,  // Loading state for component
      searchIsLoading: false     // Loading state for search button
    }
  },
  beforeMount() {
    let projectionLayersKey = 'ProjectionLayers';
    let projectionLayers = Storage.get(projectionLayersKey);

    if (projectionLayers !== null) {
      this.processLayers(projectionLayers);
      this.componentIsLoading = false;
    } else
      ApiConnector.getProjectionLayers().then(result => {
        result.json().then(output => {
          //Store item locally (if variable is set)
          let secondsStorage = parseInt(process.env.VUE_APP_KEEP_LAYER_DATA_IN_CACHE_SECONDS);
          if (isNaN(secondsStorage) === false && secondsStorage > 0)
            Storage.set(projectionLayersKey, output, secondsStorage);

          this.processLayers(output);
        });
      }).catch((error) => {
        this.$emit('onSearchClickError', GroupErrorTranslation.t(error.message, this.$i18n));
      }).finally(() => {
        this.componentIsLoading = false;
      });
  },
  mounted() {
    if (this.propInitializeTooltips === true)
      this.tooltipsSetup();
  },
  beforeUnmount() {
    if (this.propInitializeTooltips === true)
      this.tooltipsDispose();
  },
  computed: {
    isVariableSet() {
      return this.variableMap[this.variable] !== undefined;
    },
    isLastUUIDVisible() {
      let dataObject = this.createDataObject({});
      if (this.propLastUuid === null || dataObject === null)
        return false;

      return dataObject.uuid === this.propLastUuid
    },
    isFormComplete() {
      return this.isVariableSet === true &&
          this.scenarioIsAvailable(this.emissionScenario) === true &&
          this.seasonIsAvailable(this.season) === true &&
          this.yearIsAvailable(this.year) === true;
    },
    isSearchDisabled() {
      return this.isFormComplete === false ||
          this.searchIsLoading === true ||
          this.isLastUUIDVisible === true ||
          this.propBlockComponent === true;
    },
    yearInt() {
      let yearValue = parseInt(this.year);
      return isNaN(yearValue) === false ? yearValue : null
    },
  },
  methods: {
    processLayers(output) {
      this.seasonsAvailable = output.seasons || [];
      this.scenariosAvailable = output.ssps || [];

      //Iteration for each variable
      let outputVariables = output.variables || [];
      outputVariables.forEach(item => {
        let itemColorScaleData = item.color_scale;

        let colorInterface = new VariableColorConfigurationInterface();
        let variableColorPalette = colorInterface.newObject(
            itemColorScaleData.type,
            itemColorScaleData.values
        );

        let itemId = item.id;
        this.variableMap[itemId] = new VariableConfiguration(
            itemId,
            {
              en: item.name.en,
              it: item.name.it,
            },
            variableColorPalette,
            item.measure_unit_abbreviation,
            item.decimals || 0,
            item.allows_multiple_areas,
            null,
            itemColorScaleData.background_colors
        )
      });

      //Iteration for each year
      let yearsAvailable = output.years || [];
      yearsAvailable.forEach(year => {
        this.yearsAvailable.push(year.toString());
      });
    },
    createDataObject(geoJson) {
      if (this.isFormComplete === false)
        return null;

      // Create the search data object from the form fields
      return new VisualizationProjectionData(
          //Meta-data
          this.variableMap[this.variable],
          this.year,
          this.season,
          this.emissionScenario,
          // Loaded data
          geoJson,
          this.visualizationMode
      );
    },
    seasonIsAvailable(percentile) {
      return this.seasonsAvailable.indexOf(percentile) !== -1;
    },
    yearIsAvailable(year) {
      return this.yearsAvailable.indexOf(year.toString()) !== -1;
    },
    scenarioIsAvailable(rcp_value) {
      return this.scenariosAvailable.indexOf(rcp_value) !== -1;
    },
    onSearchClick() {
      if (this.isSearchDisabled) {
        // Translate message
        this.$emit('onSearchClickError', GroupErrorTranslation.t(constants.STATUS.PARAMS_MISSING, this.$i18n));
        return;
      }

      this.searchIsLoading = true;
      this.$emit('onSearchClickWait');

      ApiConnector.postProjectionQuery(this.variable, this.season, this.yearInt, this.emissionScenario).then(result => {
        result.json().then(output => {
          // Create the search data object from the form fields
          const searchData = this.createDataObject(output);

          // Emit the search data to the parent component
          this.$emit('onSearchClick', searchData);

          /*Timeout to wait some seconds before the filter window is hidden*/
          setTimeout(() => {
            this.searchIsLoading = false;
            this.$emit('afterSearchClickResult');
          }, 700);  // Stop loading
        });
      }).catch((error) => {
        // Show message based on status code
        this.$emit('onSearchClickError', GroupErrorTranslation.t(error.message, this.$i18n));
        this.$emit('afterSearchClickResult');
        this.searchIsLoading = false;
      });
    },
    tooltipsDispose() {
      disposeTooltipsByClass('.tooltip-init');
    },
    tooltipsSetup() {
      this.tooltipsDispose();
      setupTooltipsByClass('.tooltip-init');
    },
    tooltipGlossaryVariableKey() {
      return (this.isVariableSet) ? 'tooltips.GlossaryShowVariable' : 'tooltips.GlossaryShowVariablesAvailable';
    },
    tooltipGlossaryVariableName() {
      return this.isVariableSet ? this.variableMap[this.variable].getNameI18N(this.$i18n.locale) : '';
    },
    tooltipGlossaryOnClick() {
      this.$emit('tooltipGlossaryOnClick', this.variable);
    },
  },
  updated() {
    this.tooltipsSetup();
  }
}
</script>
